import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import * as favoritesApi from "../api/favorites";

const FavoritesContext = createContext();

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const prevUserRef = useRef(null);

  const loadLocalFavorites = useCallback(() => {
    try {
      const stored = localStorage.getItem("favorites");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const saveLocalFavorites = useCallback((items) => {
    try {
      localStorage.setItem("favorites", JSON.stringify(items));
    } catch {}
  }, []);

  const migrateLocalFavorites = useCallback(async () => {
    const local = loadLocalFavorites();
    if (local.length === 0) return;

    const results = await Promise.allSettled(
      local.map((item) => favoritesApi.toggleFavorite(item._id || item.id))
    );

    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length === 0) {
      localStorage.removeItem("favorites");
    }
  }, [loadLocalFavorites]);

  useEffect(() => {
    const wasLoggedIn = prevUserRef.current != null;
    const isLoggedIn = user != null;
    prevUserRef.current = user;

    if (isLoggedIn) {
      const init = async () => {
        if (!wasLoggedIn) {
          await migrateLocalFavorites();
        }
        try {
          const data = await favoritesApi.getFavorites();
          setFavorites(data);
        } catch {
          setFavorites([]);
        }
      };
      init();
    } else {
      if (wasLoggedIn) {
        setFavorites([]);
      } else {
        setFavorites(loadLocalFavorites());
      }
    }
  }, [user, loadLocalFavorites, migrateLocalFavorites]);

  const isFavorited = useCallback(
    (item) => {
      const itemId = item._id || item.id;
      return favorites.some((fav) => (fav._id || fav.id || fav) === itemId);
    },
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (item) => {
      const itemId = item._id || item.id;

      if (user) {
        const alreadyFav = isFavorited(item);
        setFavorites((prev) =>
          alreadyFav
            ? prev.filter((fav) => (fav._id || fav.id) !== itemId)
            : [...prev, item]
        );

        try {
          await favoritesApi.toggleFavorite(itemId);
          const data = await favoritesApi.getFavorites();
          setFavorites(data);
        } catch {
          setFavorites((prev) =>
            alreadyFav ? [...prev, item] : prev.filter((fav) => (fav._id || fav.id) !== itemId)
          );
        }
      } else {
        setFavorites((prev) => {
          const next = isFavorited(item)
            ? prev.filter((fav) => (fav._id || fav.id) !== itemId)
            : [...prev, item];
          saveLocalFavorites(next);
          return next;
        });
      }
    },
    [user, isFavorited, saveLocalFavorites]
  );

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorited }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
